import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import {
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretDown,
} from '@phosphor-icons/react'
import { Button, type ButtonProps } from './button'
import { cn } from '../../lib/utils'

/**
 * Carousel — a scrollable slider (Carousel / CarouselContent / CarouselItem /
 * CarouselPrevious / CarouselNext), adopted from the TailGrids carousel model
 * and built on Embla (`embla-carousel-react`, the same engine the app already
 * uses), wired to First Bite tokens and the {@link Button} component.
 *
 *   orientation  horizontal | vertical
 *   opts         Embla options (https://www.embla-carousel.com/api/options)
 *   plugins      Embla plugins
 *   setApi       receive the Embla instance for custom controls/events
 *
 * Accessible by default: the root is a `region[aria-roledescription="carousel"]`,
 * each item is a `group[aria-roledescription="slide"]`, the nav buttons carry
 * sr-only "Previous/Next slide" labels and disable at the ends, and the arrow
 * keys matching the scroll axis (←/→ horizontal, ↑/↓ vertical) scroll when focus
 * is inside.
 *
 * Vertical carousels need a bounded height — set it on `Carousel` (e.g.
 * `className="h-64"`); the viewport inherits it. The root reserves a gutter for
 * the controls automatically, so they never overlap the slides or clip.
 *
 * Note: the spec's nav-button `appearance` prop isn't part of fb-ui's `Button`,
 * so the controls render as bordered ghost icon-buttons; pass any `Button` prop
 * (variant/size/className) to restyle.
 */

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

export type { CarouselApi }

export interface CarouselProps {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

// Only what the child components actually read — deliberately NOT extending
// CarouselProps, so an inline `opts`/`plugins`/`setApi` from the consumer can't
// bust the memo and re-render every carousel child.
interface CarouselContextValue {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  orientation: 'horizontal' | 'vertical'
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null)

function useCarousel(): CarouselContextValue {
  const ctx = React.useContext(CarouselContext)
  if (!ctx) throw new Error('useCarousel must be used within <Carousel>')
  return ctx
}

export const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      { ...opts, axis: orientation === 'horizontal' ? 'x' : 'y' },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) return
      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api])
    const scrollNext = React.useCallback(() => api?.scrollNext(), [api])

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Don't hijack arrow keys from interactive content in a slide (inputs,
        // selects, contenteditable) — let them handle their own caret/selection.
        const target = e.target as HTMLElement
        if (
          target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
        ) {
          return
        }
        // Match the scroll axis: ←/→ for horizontal, ↑/↓ for vertical.
        const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'
        const nextKey =
          orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
        if (e.key === prevKey) {
          e.preventDefault()
          scrollPrev()
        } else if (e.key === nextKey) {
          e.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext, orientation]
    )

    // Hand the Embla instance back to the consumer when requested.
    React.useEffect(() => {
      if (api && setApi) setApi(api)
    }, [api, setApi])

    // Subscribe to Embla's select/reInit events to keep the nav state in sync.
    React.useEffect(() => {
      if (!api) return
      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)
      return () => {
        api.off('reInit', onSelect)
        api.off('select', onSelect)
      }
    }, [api, onSelect])

    const ctx = React.useMemo<CarouselContextValue>(
      () => ({
        carouselRef,
        api,
        orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }),
      [
        carouselRef,
        api,
        orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      ]
    )

    return (
      <CarouselContext.Provider value={ctx}>
        <div
          ref={ref}
          onKeyDown={handleKeyDown}
          // Reserve a gutter for the outside controls so they never overlap the
          // slides and never clip — the carousel is self-contained.
          className={cn(
            'relative',
            orientation === 'horizontal' ? 'px-12' : 'py-12',
            className
          )}
          role="region"
          aria-roledescription="carousel"
          // A sensible default name (APG wants carousels labelled); override with
          // your own `aria-label`/`aria-labelledby` via props.
          aria-label="Carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = 'Carousel'

export const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()
  return (
    // `h-full` for vertical so the viewport inherits the root's height — Embla
    // needs a bounded height to scroll vertically (set the height on `Carousel`).
    <div
      ref={carouselRef}
      className={cn('overflow-hidden', orientation === 'vertical' && 'h-full')}
    >
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 h-full flex-col',
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

/**
 * A single slide. Renders `role="group"` + `aria-roledescription="slide"`; per
 * the APG carousel pattern you should give each slide an accessible name via
 * `aria-label` (e.g. `aria-label="1 of 5"` or a product title) so screen readers
 * announce its identity rather than just "slide, group".
 */
export const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()
  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

// Controls sit just OUTSIDE the slide viewport (shadcn convention) so they never
// cover slide content. The `Carousel` root reserves the gutter for them
// automatically (its `px-12`/`py-12` padding), so they stay inside the
// component's box and never clip. Override the position via `className` to
// overlay them on the slides instead (e.g. full-bleed image carousels).
const navButtonClass =
  'absolute z-10 rounded-full border border-fbui-gray-200 bg-fbui-base-white shadow-sm'

export const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, variant = 'ghost', size = 'sm', ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      iconOnly
      className={cn(
        navButtonClass,
        orientation === 'horizontal'
          ? 'left-0 top-1/2 -translate-y-1/2'
          : 'top-0 left-1/2 -translate-x-1/2',
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      {orientation === 'horizontal' ? (
        <CaretLeft weight="bold" />
      ) : (
        <CaretUp weight="bold" />
      )}
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})
CarouselPrevious.displayName = 'CarouselPrevious'

export const CarouselNext = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'ghost', size = 'sm', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel()
    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        iconOnly
        className={cn(
          navButtonClass,
          orientation === 'horizontal'
            ? 'right-0 top-1/2 -translate-y-1/2'
            : 'bottom-0 left-1/2 -translate-x-1/2',
          className
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        {orientation === 'horizontal' ? (
          <CaretRight weight="bold" />
        ) : (
          <CaretDown weight="bold" />
        )}
        <span className="sr-only">Next slide</span>
      </Button>
    )
  }
)
CarouselNext.displayName = 'CarouselNext'
